import { formatDistanceToNow } from 'date-fns'
import { Link } from 'react-router-dom'
import {
  Brain,
  Fish,
  Trash2,
  Lightbulb,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import type { AIAnalysisResponse, Severity } from '../types'

// Disease code to full name mapping
const DISEASE_NAMES: Record<string, string> = {
  ICH: 'Ichthyophthirius multifiliis',
  FLU: 'Gyrodactylus species (Skin Flukes)',
  DAC: 'Dactylogyrus species (Gill Flukes)',
  COS: 'Costia necatrix',
  TRI: 'Trichodina species',
  CHI: 'Chilodonella cyprini',
  ARG: 'Argulus species (Fish Lice)',
  LER: 'Lernaea species (Anchor Worm)',
  ANI: 'Anisakis species',
  DIP: 'Diplostomum species (Eye Flukes)',
  POST: 'Posthodiplostomum species (Black Spot)',
  PRO: 'Proteocephalus species (Tapeworm)',
  BOT: 'Bothriocephalus species (Tapeworm)',
  LIG: 'Ligula intestinalis (Tapeworm)',
  PHI: 'Philometra species',
  CAM: 'Camallanus species (Red Worm)',
  CAP: 'Capillaria species',
  CON: 'Contracaecum species',
  EUS: 'Eustrongylides species',
  SPH: 'Sphincterodiplostomum species',
  AER: 'Aeromonas hydrophila',
  VIB: 'Vibrio species (Vibriosis)',
  COL: 'Flavobacterium columnare (Columnaris)',
  STR: 'Streptococcus species',
  FUN: 'Saprolegnia species (Fungal)',
  FIN: 'Fin Rot',
  ERM: 'Yersinia ruckeri (Enteric Redmouth)',
  AGD: 'Amoebic Gill Disease',
  CAR: 'Cardicola species (Blood Fluke)',
  VNN: 'Viral Nervous Necrosis',
  WSSV: 'White Spot Syndrome Virus',
  YHV: 'Yellowhead Virus',
  QXD: 'QX Disease',
  POMS: 'Pacific Oyster Mortality Syndrome',
  SDD: 'Scale Drop Disease',
  SEA: 'Sea Lice',
  EHP: 'Enterocytozoon hepatopenaei',
  AVG: 'Abalone Viral Ganglioneuritis',
  EHNV: 'Epizootic Haematopoietic Necrosis Virus',
  BON: 'Bonamiosis',
}

// Format the identified condition for subtext display
function formatIdentifiedCondition(condition: string | undefined): string | null {
  if (!condition) return null

  // Check if the condition is a known code
  const conditionUpper = condition.toUpperCase()
  const fullName = DISEASE_NAMES[conditionUpper]

  if (fullName) {
    return `${fullName} (${conditionUpper})`
  }

  // Return as-is if not a known code (might already be a full name)
  return condition
}

interface AIHistoryCardProps {
  analysis: AIAnalysisResponse
  pondName: string
  farmId: string
  onDelete: (id: string) => void
}

const SEVERITY_STYLES: Record<
  Exclude<Severity, 'none'>,
  {
    border: string
    bg: string
    badge: string
    icon: React.ElementType
  }
> = {
  critical: {
    border: 'border-l-4 border-l-red-500',
    bg: 'bg-gradient-to-r from-red-50/60 to-white',
    badge: 'bg-red-50 text-red-700',
    icon: XCircle,
  },
  high: {
    border: 'border-l-4 border-l-orange-500',
    bg: 'bg-gradient-to-r from-orange-50/40 to-white',
    badge: 'bg-orange-50 text-orange-700',
    icon: AlertTriangle,
  },
  medium: {
    border: 'border-l-4 border-l-yellow-500',
    bg: 'bg-white',
    badge: 'bg-yellow-50 text-yellow-700',
    icon: AlertCircle,
  },
  low: {
    border: 'border-l-4 border-l-green-500',
    bg: 'bg-white',
    badge: 'bg-green-50 text-green-700',
    icon: CheckCircle,
  },
}

export function AIHistoryCard({ analysis, pondName, farmId, onDelete }: AIHistoryCardProps) {
  const severity = (analysis.severity || 'medium') as Exclude<Severity, 'none'>
  const styles = SEVERITY_STYLES[severity] || SEVERITY_STYLES.medium
  const SeverityIcon = styles.icon
  const isRootCause = analysis.analysisType === 'root_cause'
  const TypeIcon = isRootCause ? Brain : Fish
  const title = isRootCause ? 'Root Cause Analysis' : 'Fish Health Diagnosis'
  const identifiedCondition = formatIdentifiedCondition(analysis.identifiedCondition)
  const recommendationCount = analysis.recommendations?.length || 0

  return (
    <Link
      to={`/farms/${farmId}/ai/${analysis.id}`}
      className={`block rounded-xl border border-slate-200/50 p-4 shadow-sm transition-all hover:shadow-md ${styles.border} ${styles.bg}`}
    >
      {/* Top row: Severity + Delete */}
      <div className="flex items-center justify-between mb-3">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles.badge}`}
        >
          <SeverityIcon className="h-3 w-3" />
          {severity.charAt(0).toUpperCase() + severity.slice(1)}
        </span>
        <button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (confirm('Delete this analysis?')) {
              onDelete(analysis.id)
            }
          }}
          className="p-1 rounded text-slate-400 hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Title with type icon */}
      <div className="flex items-start gap-2 mb-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-50">
          <TypeIcon className="h-3.5 w-3.5 text-emerald-600" />
        </div>
        <div className="min-w-0">
          <h3 className="font-semibold text-slate-900 leading-tight text-sm">
            {title}
          </h3>
          {identifiedCondition && (
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
              Identified: {identifiedCondition}
            </p>
          )}
        </div>
      </div>

      {/* Pond name */}
      <p className="text-sm text-slate-600 mb-2">{pondName}</p>

      {/* Footer: Confidence, actions count, date */}
      <div className="flex items-center gap-3 text-xs text-slate-500">
        {analysis.confidenceScore && (
          <span className="font-medium">
            {Math.round(analysis.confidenceScore * 100)}%
          </span>
        )}
        {recommendationCount > 0 && (
          <span className="flex items-center gap-1">
            <Lightbulb className="h-3 w-3" />
            {recommendationCount}
          </span>
        )}
        <span className="ml-auto">
          {formatDistanceToNow(new Date(analysis.createdAt), { addSuffix: true })}
        </span>
      </div>
    </Link>
  )
}

export default AIHistoryCard
